import { useState, useRef, useEffect, useCallback } from 'react';
import api from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { issueResultsHref, zebraSearchHref, handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import { QUICK_SEARCH_FIELDS, QUICK_SEARCH_DEBOUNCE_MS, escapeJql, quickSearchJql } from '@modules/zebra-search/frontend/utils/quickSearch';
import '@modules/zebra-search/frontend/components/ZebraSearchBar/ZebraSearchBar.css';

const SEARCH_FIELDS = QUICK_SEARCH_FIELDS;
const MAX_RESULTS = 8;
const MAX_RECENT = 8;
const RECENT_CACHE_TTL = 2 * 60 * 1000;
const DEBOUNCE_MS = QUICK_SEARCH_DEBOUNCE_MS;
// The Zebra Search page's starting query: items created in the last 30
// days, newest first. startOfDay("-30d") rather than the bare `-30d`
// shorthand: the ZQL engine can't tokenise `-30d` but understands the
// function form, and Jira accepts it too — same "last 30 days".
const ZEBRA_SEARCH_DEFAULT_JQL = 'created >= startOfDay("-30d") order by created DESC';

// The query for what was typed — escapeJql / classifyTerm / quickSearchJql
// live in utils/quickSearch.js, shared with Home's Basic search.

function getStatusColor(colorName) {
  if (colorName === 'green') return 'var(--success)';
  if (colorName === 'yellow') return '#4a90d9';
  if (colorName === 'blue-gray') return '#6b7280';
  return 'var(--accent)';
}

function getIssueTypeDot(issueTypeName) {
  const name = (issueTypeName || '').toLowerCase();
  if (name.includes('bug')) return '#e5493a';
  if (name.includes('epic')) return '#904ee2';
  if (name.includes('story')) return '#63ba3c';
  if (name.includes('sub')) return '#4bade8';
  return '#4a90d9';
}

// Ctrl on Windows/Linux, ⌘ on Mac — shown on the collapsed trigger so the
// shortcut is discoverable without spending header width on a full input.
const SHORTCUT_LABEL = (typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || ''))
  ? '⌘K' : 'Ctrl K';

export default function ZebraSearchBar() {
  const { user } = useAuth();
  const { openTab } = useTab();
  // Collapsed by default: the bar is a compact pill until the user asks for
  // it, which hands the width back to the navigation menus beside it. It
  // expands on click / Ctrl-K and collapses again once it's empty and
  // unfocused, so a typed query is never thrown away by a stray click.
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentItems, setRecentItems] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [showRecent, setShowRecent] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestCountRef = useRef(0);
  const dropdownRef = useRef(null);
  const recentCacheRef = useRef({ items: null, timestamp: 0 });
  // Mirrors `query` for the document-level outside-click listener, which is
  // registered once and would otherwise close over the initial empty string.
  const queryRef = useRef('');
  queryRef.current = query;
  // Lowercased set of real project keys, loaded once (see effect below), used
  // by classifyTerm to route key lookups down the fast path. null until loaded.
  const projectKeysRef = useRef(null);

  // Position the dropdown using getBoundingClientRect. Right-anchored to the
  // bar (the bar itself sits at the right of the header now), clamped so it
  // can never run off either edge of the viewport.
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current || !dropdownRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdown = dropdownRef.current;
    const dropdownWidth = Math.min(520, window.innerWidth - 24);

    let left = rect.right - dropdownWidth;
    if (left + dropdownWidth > window.innerWidth - 12) {
      left = window.innerWidth - dropdownWidth - 12;
    }
    if (left < 12) left = 12;

    dropdown.style.width = `${dropdownWidth}px`;
    dropdown.style.top = `${rect.bottom + 6}px`;
    dropdown.style.left = `${left}px`;
  }, []);

  // Fetch recently updated items for the current user
  const fetchRecentItems = useCallback(async () => {
    if (!user?.accountId) return;

    const cache = recentCacheRef.current;
    // Use the cache only if it's fresh AND nothing has been viewed since it was
    // built (viewing an item bumps recentlyViewedInvalidatedAt), so a newly
    // opened issue shows up without a page reload.
    const invalidatedAt = api.recentlyViewedInvalidatedAt ? api.recentlyViewedInvalidatedAt() : 0;
    if (cache.items && Date.now() - cache.timestamp < RECENT_CACHE_TTL && cache.timestamp >= invalidatedAt) {
      setRecentItems(cache.items);
      setShowRecent(true);
      return;
    }

    setRecentLoading(true);
    setShowRecent(true);
    try {
      // Per-account recently-viewed history (server-side, keyed by accountId;
      // see modules/recently-viewed/). Preferred path: one call to the slim
      // /items endpoint, which resolves the viewed keys to display-ready
      // issues server-side and skips the heavy per-issue loads (comments/
      // attachments/links/full EAV) the dropdown never renders.
      //
      // Falls back to the legacy two-call path (getRecentlyViewed keys +
      // searchIssues) if that endpoint is unavailable, so the dropdown keeps
      // working regardless. Either way, an empty history shows the most-
      // recently-updated items so it's never blank.
      let items = null;
      try {
        items = await api.getRecentlyViewedItems();
      } catch (slimErr) {
        items = null; // fall through to legacy path below
      }

      if (items === null) {
        // Legacy path — keys, then a key-in(...) search.
        const keys = await api.getRecentlyViewed();
        if (keys && keys.length) {
          const topKeys = keys.slice(0, MAX_RECENT);
          const jql = `key in (${topKeys.map(k => `"${escapeJql(k)}"`).join(',')})`;
          const data = await api.searchIssues(jql, SEARCH_FIELDS, MAX_RECENT);
          const byKey = new Map((data.issues || []).map(i => [i.key, i]));
          items = topKeys.map(k => byKey.get(k)).filter(Boolean); // preserve viewed order
        } else {
          items = [];
        }
      }

      if (!items.length) {
        // No history yet (or nothing resolved) — most-recently-updated.
        const data = await api.searchIssues(`ORDER BY updated DESC`, SEARCH_FIELDS, MAX_RECENT);
        items = data.issues || [];
      } else {
        items = items.slice(0, MAX_RECENT);
      }

      recentCacheRef.current = { items, timestamp: Date.now() };
      setRecentItems(items);
    } catch (err) {
      console.error('Failed to fetch recent items:', err);
      setRecentItems([]);
    } finally {
      setRecentLoading(false);
    }
  }, [user?.accountId]);

  // Load the real project-key list once so classifyTerm can distinguish an
  // issue key ("YED2401-…") from a summary part number that only looks
  // key-shaped ("FT200-0001", "TX65"). One cheap call; failure just leaves the
  // ref null and searches default to the safe key+summary path.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.getProjects();
        const values = Array.isArray(data) ? data : (data?.values || []);
        const set = new Set(values.map(p => String(p?.key || '').toLowerCase()).filter(Boolean));
        if (alive && set.size) projectKeysRef.current = set;
      } catch { /* leave null → default to text search */ }
    })();
    return () => { alive = false; };
  }, []);

  // Search handler
  const doSearch = useCallback(async (term) => {
    if (!term.trim()) {
      setResults([]);
      setIsOpen(false);
      setError(null);
      return;
    }

    const requestId = ++requestCountRef.current;
    setLoading(true);
    setError(null);
    setIsOpen(true);

    try {
      const trimmed = term.trim();
      // Pick the cheapest query for what was typed (see classifyTerm). A term
      // whose prefix matches a real project key scans the issue_key column
      // only; a word (or a key-shaped part number) still searches summaries via
      // `text ~`. The old query ALWAYS ran `(key ~ OR text ~)`, so even a full
      // issue key paid the per-row EAV summary subquery — the multi-second wait.
      //
      // Key path: NO explicit ORDER BY. A broad prefix like "YED" matches many
      // rows, and `ORDER BY updated` is itself a per-row EAV subquery — sorting
      // thousands of matches that way is the other half of the slowness. The
      // engine's default order (work_item_id DESC ≈ newest first) needs no EAV
      // and is a fine order for a picker. Text path keeps updated-order.
      const jql = quickSearchJql(trimmed, projectKeysRef.current);
      // skipTotal — the dropdown shows a list, never a count, so drop the
      // parallel COUNT query (halves backend work on every keystroke).
      const data = await api.searchIssues(jql, SEARCH_FIELDS, MAX_RESULTS, null, 0, true);
      if (requestId !== requestCountRef.current) return;
      setResults(data.issues || []);
      setSelectedIndex(-1);
    } catch (err) {
      if (requestId !== requestCountRef.current) return;
      setResults([]);
      setError('Search failed. Try a different query.');
    } finally {
      if (requestId === requestCountRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Debounced input handler
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      setIsOpen(false);
      setError(null);
      setLoading(false);
      setShowRecent(false);
      fetchRecentItems();
      return;
    }
    setShowRecent(false);
    debounceRef.current = setTimeout(() => doSearch(value), DEBOUNCE_MS);
  }, [doSearch, fetchRecentItems]);

  // Open result in work item results tab
  const openResult = useCallback((issueKey) => {
    const params = new URLSearchParams({ jql: `key = ${issueKey}` });
    openTab('/issues?' + params.toString(), issueKey);
    setIsOpen(false);
    setShowRecent(false);
    setQuery('');
    setResults([]);
    setExpanded(false); // job done — hand the header width back
    inputRef.current?.blur();
  }, [openTab]);

  // Mirror Jira's "Search Jira for work items" affordance — opens the
  // Zebra Search tab pre-loaded with a default JQL listing items
  // created in the last 30 days, newest first. The JQL is editable on
  // the Zebra Search page itself so this is just a sensible starting
  // point.
  const openZebraSearch = useCallback(() => {
    openTab(zebraSearchHref(ZEBRA_SEARCH_DEFAULT_JQL), 'Zebra Search');
    setIsOpen(false);
    setShowRecent(false);
    setQuery('');
    setResults([]);
    setExpanded(false);
    inputRef.current?.blur();
  }, [openTab]);

  // Clear search
  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setError(null);
    setLoading(false);
    clearTimeout(debounceRef.current);
    inputRef.current?.focus();
    fetchRecentItems();
  }, [fetchRecentItems]);

  // Expand + focus. The input mounts with this state change, so focus has to
  // wait for the commit — the effect below does that.
  const expand = useCallback(() => {
    setExpanded(true);
    fetchRecentItems();
  }, [fetchRecentItems]);

  const collapse = useCallback(() => {
    setExpanded(false);
    setIsOpen(false);
    setShowRecent(false);
    setQuery('');
    setResults([]);
    setError(null);
    clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    // Focus after the input has actually rendered.
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [expanded]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      // Escape always gives the header its space back.
      collapse();
      return;
    }

    const activeItems = showRecent ? recentItems : results;
    const isAnyOpen = showRecent || isOpen;
    if (!isAnyOpen || activeItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < activeItems.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : activeItems.length - 1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      openResult(activeItems[selectedIndex].key);
    }
  }, [isOpen, showRecent, results, recentItems, selectedIndex, openResult, collapse]);

  // Global Ctrl+K / Cmd+K shortcut — expands the bar if it's collapsed.
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        expand();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [expand]);

  // Click outside closes the dropdown, and collapses the bar back to its pill
  // when there's nothing typed. A query in progress keeps the bar open so a
  // misclick can't discard it.
  useEffect(() => {
    const handleMouseDown = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (inContainer || inDropdown) return;
      setIsOpen(false);
      setShowRecent(false);
      if (!queryRef.current.trim()) setExpanded(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Update dropdown position when open
  useEffect(() => {
    if (isOpen || showRecent) {
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      return () => window.removeEventListener('resize', updateDropdownPosition);
    }
  }, [isOpen, showRecent, updateDropdownPosition]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleFocus = useCallback(() => {
    if (query.trim() && results.length > 0) {
      setIsOpen(true);
    } else if (!query.trim()) {
      fetchRecentItems();
    }
  }, [query, results.length, fetchRecentItems]);

  const renderIssueRow = (issue, idx) => {
    const statusName = issue.fields?.status?.name || '';
    const statusColor = issue.fields?.status?.statusCategory?.colorName;
    const issueTypeName = issue.fields?.issuetype?.name || '';
    const issueTypeIcon = issue.fields?.issuetype?.iconUrl;
    const projectKey = issue.fields?.project?.key || '';
    const summary = issue.fields?.summary || '';

    // A real link: middle-click / Ctrl-click open the item in a new browser
    // tab; a plain click stays in the app (handleIssueLinkActivation).
    return (
      <a
        key={issue.key}
        href={issueResultsHref(issue.key)}
        className={`jira-search-result ${idx === selectedIndex ? 'selected' : ''}`}
        onClick={(e) => handleIssueLinkActivation(e, openResult, issue.key)}
        onMouseEnter={() => setSelectedIndex(idx)}
      >
        {issueTypeIcon ? (
          <img
            className="jira-search-type-icon"
            src={api.getJiraImageUrl(issueTypeIcon)}
            alt={issueTypeName}
            title={issueTypeName}
            width="16"
            height="16"
          />
        ) : (
          <span
            className="jira-search-type-dot"
            style={{ backgroundColor: getIssueTypeDot(issueTypeName) }}
            title={issueTypeName}
          />
        )}
        <span className="jira-search-key">{issue.key}</span>
        <span className="jira-search-summary" title={summary}>{summary}</span>
        <span
          className="jira-search-status"
          style={{ backgroundColor: getStatusColor(statusColor) }}
        >
          {statusName}
        </span>
        <span className="jira-search-project">{projectKey}</span>
      </a>
    );
  };

  const showRecentDropdown = showRecent && !query.trim() && (recentLoading || recentItems.length > 0);
  const showSearchDropdown = isOpen && (loading || error || results.length > 0 || query.trim());
  // Show the dropdown whenever the input is focused so the
  // "Open Zebra Search" row is always reachable, even before the user
  // has typed anything or while recent items are still loading.
  const showZebraSearchEntry = isOpen || showRecent;
  const showDropdown = expanded && (showRecentDropdown || showSearchDropdown || showZebraSearchEntry);

  return (
    <div className={`jira-search-bar ${expanded ? 'is-expanded' : 'is-collapsed'}`} ref={containerRef}>
      {/* The inner element persists across the collapse/expand swap, so its
          width transition animates rather than snapping. */}
      <div className="jira-search-bar-inner">
        {expanded ? (
          <div className="jira-search-input-wrapper">
            <svg className="jira-search-icon" viewBox="0 0 20 20" width="18" height="18">
              <path fill="currentColor" fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.45 4.39l3.58 3.58a.75.75 0 1 1-1.06 1.06l-3.58-3.58A7 7 0 0 1 2 9Z" clipRule="evenodd"/>
            </svg>
            <input
              ref={inputRef}
              className="jira-search-input"
              type="text"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              placeholder="Search all work items..."
              spellCheck={false}
            />
            {query && (
              <button className="jira-search-clear" onClick={handleClear} title="Clear search">
                <svg viewBox="0 0 16 16" width="14" height="14">
                  <path fill="currentColor" d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z"/>
                </svg>
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="jira-search-trigger"
            onClick={expand}
            title={`Search all work items (${SHORTCUT_LABEL})`}
            aria-label="Search all work items"
            aria-expanded={false}
          >
            <svg className="jira-search-icon" viewBox="0 0 20 20" width="17" height="17">
              <path fill="currentColor" fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.45 4.39l3.58 3.58a.75.75 0 1 1-1.06 1.06l-3.58-3.58A7 7 0 0 1 2 9Z" clipRule="evenodd"/>
            </svg>
            <span className="jira-search-trigger-label">Search</span>
            <kbd className="jira-search-trigger-kbd">{SHORTCUT_LABEL}</kbd>
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="jira-search-dropdown" ref={dropdownRef}>
          {/* Always-on entry: jump to the full Zebra Search page with a
              30-day-default JQL pre-loaded. Mirrors Jira's "Search for
              work items" link. */}
          <a
            className="jira-search-zebra-entry"
            href={zebraSearchHref(ZEBRA_SEARCH_DEFAULT_JQL)}
            onClick={(e) => handleIssueLinkActivation(e, openZebraSearch)}
            onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault(); openZebraSearch(); } }}
          >
            <svg className="jira-search-zebra-entry-icon" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
              <path fill="currentColor" fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.45 4.39l3.58 3.58a.75.75 0 1 1-1.06 1.06l-3.58-3.58A7 7 0 0 1 2 9Z" clipRule="evenodd"/>
            </svg>
            <span className="jira-search-zebra-entry-label">Open Zebra Search</span>
            <span className="jira-search-zebra-entry-hint">Browse all work items</span>
          </a>
          {showRecentDropdown && (
            <>
              {recentLoading && (
                <div className="jira-search-loading">
                  <div className="jira-search-spinner" />
                  <span>Loading recent items...</span>
                </div>
              )}
              {!recentLoading && recentItems.length > 0 && (
                <>
                  <div className="jira-search-section-header">
                    <svg className="jira-search-section-icon" viewBox="0 0 16 16" width="12" height="12">
                      <path fill="currentColor" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0Zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM8 3a.75.75 0 0 1 .75.75v3.69l2.28 2.28a.75.75 0 1 1-1.06 1.06l-2.5-2.5A.75.75 0 0 1 7.25 8V3.75A.75.75 0 0 1 8 3Z"/>
                    </svg>
                    Recently Viewed
                  </div>
                  {recentItems.map((issue, idx) => renderIssueRow(issue, idx))}
                </>
              )}
            </>
          )}
          {showSearchDropdown && !showRecentDropdown && (
            <>
              {loading && (
                <div className="jira-search-loading">
                  <div className="jira-search-spinner" />
                  <span>Searching...</span>
                </div>
              )}
              {error && !loading && (
                <div className="jira-search-error">{error}</div>
              )}
              {!loading && !error && results.length === 0 && query.trim() && (
                <div className="jira-search-empty">No results found</div>
              )}
              {!loading && !error && results.map((issue, idx) => renderIssueRow(issue, idx))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
